<script setup lang="ts">
import { ref, onMounted } from 'vue';
import OfferTutorial from './OfferTutorial.vue';
import type { TutorialStep } from '../offer-tutorial';
const props = defineProps<{ editable?: boolean }>();
type Menu = { id: string; title: string; steps: TutorialStep[] };
const menus = ref<Menu[]>([]),
  error = ref(''),
  loading = ref(true),
  creating = ref(false),
  title = ref(''),
  createdId = ref('');
const panels = ref<Record<string, 'tuto' | 'edit' | 'settings'>>({});
async function load(): Promise<void> {
  if (!menus.value.length) loading.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      (props.editable ? '/admin' : '/provider') + '/api/team-tutorials',
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Unavailable');
    const body = (await response.json()) as { tutorials?: Menu[] };
    if (!Array.isArray(body.tutorials)) throw new Error('Unavailable');
    const current = new Map(menus.value.map((menu) => [menu.id, menu]));
    menus.value = body.tutorials.map((menu) => {
      const previous = current.get(menu.id);
      // Keep unchanged inputs stable so another menu's refresh cannot erase an open draft.
      return previous &&
        previous.title === menu.title &&
        JSON.stringify(previous.steps) === JSON.stringify(menu.steps)
        ? previous
        : menu;
    });
  } catch {
    error.value = 'Tutoriels indisponibles. Réessayez.';
  } finally {
    loading.value = false;
  }
}
async function create(): Promise<void> {
  if (creating.value || !title.value.trim()) return;
  creating.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/team-tutorials', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title.value }),
    });
    if (!response.ok) throw new Error('Refused');
    const row = (await response.json()) as Menu;
    await load();
    createdId.value = row.id;
    panels.value[row.id] = 'edit';
    title.value = '';
  } catch {
    error.value = 'Création impossible. Vérifiez le nom de la rubrique.';
  } finally {
    creating.value = false;
  }
}
onMounted(load);
</script>
<template>
  <section class="team-tutorial-library" aria-label="Tutoriels et réglages prestataires">
    <h2>Tutoriels et réglages</h2>
    <form v-if="editable" class="tutorial-create" @submit.prevent="create">
      <label for="new-team-tutorial">Nom de la nouvelle rubrique</label>
      <input
        id="new-team-tutorial"
        v-model="title"
        maxlength="120"
        required
        placeholder="Ex. Réglage du cadenas"
        :disabled="creating"
      />
      <button class="button button-assign" type="submit" :disabled="creating || !title.trim()">
        {{ creating ? 'Création…' : 'Créer une rubrique' }}
      </button>
    </form>
    <p v-if="loading" role="status">Chargement des tutoriels…</p>
    <template v-if="error"
      ><p role="alert">{{ error }}</p>
      <button class="button button-secondary" type="button" @click="load">
        Réessayer les tutoriels
      </button></template
    >
    <details
      v-for="menu in menus"
      :key="menu.id"
      :open="createdId === menu.id"
      class="offer-tutorial tutorial-menu"
      :data-tutorial-id="menu.id"
    >
      <summary>{{ menu.title }}</summary>
      <div class="tutorial-menu-actions">
        <button
          type="button"
          class="button tutorial-view-button"
          :aria-pressed="panels[menu.id] === 'tuto'"
          @click="panels[menu.id] = 'tuto'"
        >
          Tuto
        </button>
        <button
          type="button"
          class="button tutorial-modify-button"
          :disabled="!editable && menu.id !== 'physical-box'"
          :aria-pressed="panels[menu.id] === (editable ? 'edit' : 'settings')"
          @click="panels[menu.id] = editable ? 'edit' : 'settings'"
        >
          Modifier
        </button>
      </div>
      <p v-if="!editable && menu.id !== 'physical-box'" class="tutorial-setting-hint">
        Aucun réglage dans l’application pour cette rubrique. Consultez le tuto pour agir sur
        l’équipement.
      </p>
      <OfferTutorial
        v-if="panels[menu.id] === 'tuto'"
        :steps="menu.steps"
        :tutorial-id="menu.id"
        :title="menu.title"
      />
      <OfferTutorial
        v-if="editable && panels[menu.id] === 'edit'"
        :steps="menu.steps"
        :tutorial-id="menu.id"
        :title="menu.title"
        editable
        @updated="load"
      />
      <slot
        v-if="!editable && menu.id === 'physical-box' && panels[menu.id] === 'settings'"
        name="box-settings"
      />
    </details>
  </section>
</template>
